import React, { MouseEvent, useMemo } from 'react';
import CustomLink from './CustomLink';
import ExternalLink from './ExternalLink';
import InternalLink from './InternalLink';
import { isExternal } from './utils';

enum LinkType {
  CUSTOM_LINK = 'customLink',
  EXTERNAL_LINK = 'externalLink',
  INTERNAL_LINK = 'internalLink',
}

interface IProps {
  children?: React.ReactNode;
  className?: string;
  clearQuery?: boolean;
  collection?: string;
  'data-testid'?: string;
  href?: string;
  key?: string;
  onClick?: (event: MouseEvent, href?: string) => void;
  plain?: boolean;
  query?: Record<string, string | undefined>;
  rel?: string;
  sameTab?: boolean;
  slug?: string;
  target?: string;
  title?: string;
}

const Link = ({
  children,
  className,
  clearQuery,
  collection,
  href,
  onClick,
  plain,
  query,
  rel,
  sameTab,
  slug,
  target,
  title,
  key,
  ...props
}: IProps) => {

  if (collection || slug) {
    // tslint:disable:next-line no-console
    console.warn('Warning! If you want to provide a slug and collection, use ObjectLink :)');
  }
  const linkType = useMemo<LinkType>(() => {
    if (!href && !query) {
      return LinkType.CUSTOM_LINK;
    } else if (href && isExternal(href)) {
      return LinkType.EXTERNAL_LINK;
    } else {
      return LinkType.INTERNAL_LINK;
    }
  }, [href, query]);

  const isGeoFencedLink = useMemo(() => {
    if (!window.INIT.GEO_FENCED_SLUGS) {
      return false;
    }
    // geoBlocked channels should go through external link to hit fastly
    const isGeoBlocked = window.INIT.GEO_FENCED_SLUGS.find((path: string) => {
      if (!href) {
        return false;
      }
      return href.includes(path);
    });
    return !!isGeoBlocked;
  }, [href]);

  switch (linkType) {
    case LinkType.CUSTOM_LINK: {
      return (
        <CustomLink
          children={children}
          className={className!}
          data-testid={props['data-testid']}
          onLinkClick={onClick}
          plain={plain!}
          title={title}
          key={key}
        />
      );
    }
    case LinkType.EXTERNAL_LINK: {
      return (
        <ExternalLink
          children={children}
          className={className!}
          data-testid={props['data-testid']}
          href={href}
          onLinkClick={onClick}
          plain={plain!}
          query={query}
          rel={rel}
          sameTab={href?.includes(window.location.host) || sameTab}
          target={target}
          title={title}
          key={key}
        />
      );
    }
    case LinkType.INTERNAL_LINK: {
      return (
        <InternalLink
          children={children}
          className={className!}
          clearQuery={clearQuery}
          data-testid={props['data-testid']}
          href={href}
          onLinkClick={onClick}
          plain={plain!}
          query={query}
          sameTab={isGeoFencedLink ? false : sameTab}
          target={isGeoFencedLink ? '_blank' : target}
          title={title}
          key={key}
        />
      );
    }
    default: {
      return null;
    }
  }
};

Link.defaultProps = {
  children: null,
  className: '',
  clearQuery: false,
  collection: undefined,
  href: '',
  onClick: undefined,
  plain: false,
  query: undefined,
  rel: undefined,
  sameTab: undefined,
  slug: undefined,
  // Override default "target" attribute (_blank for external, empty for internal)
  target: undefined,
  title: undefined,
};

export default Link;
